import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {EventServiceResponse, UserSearchResult} from '../../../../../../../../../../../../@shared/models/response.module';
import {EventsService} from '../../../../../../../../../../../../@shared/services/events-service';
import {ToastService} from '../../../../../../../../../../../../@core/services/toast.service';
import {EndpointService} from '../../../../../../../../../../../../@shared/endpoints.service';
import {debounceTime, distinctUntilChanged, switchMap, Subject} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-add-service-registration',
  standalone: false,
  templateUrl: './add-service-registration.html',
  styleUrl: './add-service-registration.css'
})
export class AddServiceRegistration implements OnInit {
  @Input() eventService!: EventServiceResponse;

  // Member type selection
  memberType: 'existing' | 'manual' = 'existing';

  // User search
  userSearchQuery = '';
  userSearchResults: UserSearchResult[] = [];
  selectedUser: UserSearchResult | null = null;
  isSearchingUsers = false;
  private searchSubject = new Subject<string>();

  // Manual user entry
  firstName = '';
  lastName = '';
  email = '';
  phone = '';

  // Quantity
  numRegistrations = 1;

  // Payment options
  paymentType: 'manual' | 'send_link' = 'manual';
  paymentStatus: 'not_required' | 'pay_later' | 'paid' = 'paid';
  paymentMethod: 'cash' | 'bank_transfer' | 'other' = 'cash';

  // Submission
  submitting = false;
  submitted = false;
  errorMessage = '';

  // Validation helpers
  get isFirstNameInvalid(): boolean {
    return this.submitted && this.memberType === 'manual' && !this.firstName.trim();
  }

  get isLastNameInvalid(): boolean {
    return this.submitted && this.memberType === 'manual' && !this.lastName.trim();
  }

  constructor(
    public activeModal: NgbActiveModal,
    private eventsService: EventsService,
    private endpointService: EndpointService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    // Setup debounced user search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (query.length < 2) {
          return [];
        }
        this.isSearchingUsers = true;
        return this.endpointService.aqh_eve_67({ search: query });
      })
    ).subscribe({
      next: (response: any) => {
        this.isSearchingUsers = false;
        this.userSearchResults = response?.data || [];
      },
      error: () => {
        this.isSearchingUsers = false;
        this.userSearchResults = [];
      }
    });
  }

  onMemberTypeChange() {
    // Reset fields when switching types
    this.selectedUser = null;
    this.userSearchQuery = '';
    this.userSearchResults = [];
    this.firstName = '';
    this.lastName = '';
    this.email = '';
    this.phone = '';
    this.errorMessage = '';
  }

  onUserSearch() {
    this.searchSubject.next(this.userSearchQuery);
  }

  selectUser(user: UserSearchResult) {
    this.selectedUser = user;
    this.userSearchQuery = user.full_name || `${user.first_name} ${user.last_name}`;
    this.userSearchResults = [];
  }

  clearSelectedUser() {
    this.selectedUser = null;
    this.userSearchQuery = '';
  }

  close() {
    this.activeModal.dismiss();
  }

  isValid(): boolean {
    if (this.memberType === 'existing') {
      return this.selectedUser !== null;
    } else {
      return this.firstName.trim().length > 0 && this.lastName.trim().length > 0;
    }
  }

  submit() {
    this.submitted = true;

    if (!this.isValid()) {
      this.toastService.error(this.translateService.instant('manageEvent.addServiceRegistration.validationError'));
      return;
    }

    // For send_link, we need email
    if (this.paymentType === 'send_link') {
      const userEmail = this.memberType === 'existing' ? this.selectedUser?.email : this.email;
      if (!userEmail) {
        this.errorMessage = this.translateService.instant('manageEvent.addServiceRegistration.emailRequired');
        return;
      }
    }

    this.submitting = true;
    this.errorMessage = '';

    const payload: any = {
      event_service_id: this.eventService.event_service_id,
      num_registrations: this.numRegistrations,
      // Payment options
      payment_type: this.paymentType,
      // Use 'pay_later' for send_link (confirmed but awaiting payment)
      payment_status: this.paymentType === 'manual' ? this.paymentStatus : 'pay_later',
      payment_method: this.paymentType === 'manual' ? this.paymentMethod : undefined,
      send_payment_link: this.paymentType === 'send_link'
    };

    if (this.memberType === 'existing' && this.selectedUser) {
      payload.user_id = this.selectedUser.user_id;
    } else {
      // Manual entry - include user details for backend to create/find user
      payload.first_name = this.firstName.trim();
      payload.last_name = this.lastName.trim();
      payload.email = this.email.trim() || undefined;
      payload.phone = this.phone.trim() || undefined;
    }

    this.eventsService.createServiceRegistration(payload).subscribe({
      next: () => {
        if (this.paymentType === 'send_link') {
          this.toastService.success(this.translateService.instant('manageEvent.addServiceRegistration.successWithLink'));
        } else {
          this.toastService.success(this.translateService.instant('manageEvent.addServiceRegistration.success'));
        }
        this.activeModal.close(true);
      },
      error: (err) => {
        this.submitting = false;
        this.errorMessage = err.error?.reason || this.translateService.instant('manageEvent.addServiceRegistration.error');
      }
    });
  }

  formatPrice(price: number, currency: string): string {
    return `${price.toFixed(2)} ${currency}`;
  }
}
